/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {environment} from '../../../environments/environment';
import {OrderLineService} from '../../services/order-line.service';
import {Page} from '../../model/page';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {ConfirmationService} from 'primeng/api';
import {OrderHeadersService} from '../../services/order-headers.service';
import {permissions} from '../../permissions';
import {Table} from 'primeng/table';
import * as _ from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {ExportModalComponent} from '../../components/export-modal';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
  selector: 'app-order-lines',
  templateUrl: './order-lines.component.html',
  styleUrls: ['./order-lines.component.scss'],
  providers: [
      OrderLineService,
      OrderHeadersService
  ]
})
export class OrderLinesComponent implements OnInit, OnDestroy {

    @ViewChild('linesTable') linesTable: Table;

    rows = new Array<object>();
    totalRecords = 0;
    selected = [];
    cols = [];
    public loading = false;
    page = new Page();

    errors = [];
    public permissions = permissions;
    public showFilters: boolean;
    public filtersApplied: boolean;

    private lineStatuses = this.orderLineService.getStatuses();
    private refOrderLineService = OrderLineService;

    private filters: { [key: string]: string; } = {};
    // private units: Array<any> = [];
    private vendors: Array<any> = [];
    private filteredVendors: any;
    // private filteredUnits: any;
    private subscriptions: Array<Subscription>;

    constructor(
        private router: Router,
        private orderLineService: OrderLineService,
        private orderHeadersService: OrderHeadersService,
        private route: ActivatedRoute,
        private toastr: ToastrService,
        private confirmationService: ConfirmationService,
        public  dialog: MatDialog,
        private title: Title
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.title.setTitle('EDI - PO Lines');
    }

    ngOnInit() {
        this.showFilters = false;
        this.filtersApplied = false;
        this.cols = [
            {
                field: 'PO1_NUMBER', align: 'left', sortable: true,
                bigScreen: {header: 'PO Number', visible: true, width: '100px'},
                smallScreen: {header: 'PO #', visible: true, width: '100px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_LINE', align: 'center', sortable: true,
                bigScreen: {header: 'Line #', visible: true, width: '65px'},
                smallScreen: {header: 'Line#', visible: true, width: '65px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'CO1_NAME', align: 'left', sortable: true,
                bigScreen: {header: 'Company', visible: true, width: '120px'},
                smallScreen: {header: 'Company', visible: true, width: '120px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'VD1_NAME', align: 'left', sortable: true,
                bigScreen: {header: 'Vendor', visible: true, width: '140px'},
                smallScreen: {header: 'Item Vendor', visible: true, width: '140px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_STATUS', align: 'left', sortable: true,
                bigScreen: {header: 'Line Status', visible: true, width: '110px'},
                smallScreen: {header: 'Status', visible: true, width: '110px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_ITEM_ID', align: 'left', sortable: false,
                bigScreen: {header: 'Item ID', visible: true, width: '125px'},
                smallScreen: {header: 'Item ID', visible: true, width: '125px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_SUPPLIER_PART_NUMBER', align: 'left', sortable: false,
                bigScreen: {header: 'Supplier Part #', visible: true, width: '125px'},
                smallScreen: {header: 'Part #', visible: true, width: '125px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_QUANTITY', align: 'center', sortable: true,
                bigScreen: {header: 'ORD Qty', visible: true, width: '50px'},
                smallScreen: {header: 'ORD Qty', visible: true, width: '50px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_QUANTITY_NEW', align: 'center', sortable: true,
                bigScreen: {header: 'CNF Qty', visible: true, width: '50px'},
                smallScreen: {header: 'CNF Qty', visible: true, width: '50px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_TOTAL_SHIPPED', align: 'center', sortable: true,
                bigScreen: {header: 'SHP Qty', visible: true, width: '50px'},
                smallScreen: {header: 'SHP Qty', visible: true, width: '50px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_TOTAL_INVOICED', align: 'center', sortable: true,
                bigScreen: {header: 'INV Qty', visible: true, width: '50px'},
                smallScreen: {header: 'INV Qty', visible: true, width: '50px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_UNIT_PRICE', align: 'right', sortable: true,
                bigScreen: {header: 'Unit Price', visible: true, width: '80px'},
                smallScreen: {header: 'Unit Price', visible: true, width: '80px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'EXT_VAL', align: 'right', sortable: false,
                bigScreen: {header: 'Ext Value', visible: false, width: '90px'},
                smallScreen: {header: 'Ext Val', visible: false, width: '90px'},
                export: {visible: false, checked: false}
            },
            {
                field: 'PO1_CURRENCY', align: 'center', sortable: false,
                bigScreen: {header: 'Currency', visible: true, width: '80px'},
                smallScreen: {header: 'Currency', visible: true, width: '80px'},
                export: {visible: true, checked: true}
            },
            // {
            //     field: 'UM1_ID_NEW', align: 'center', sortable: false,
            //     bigScreen: {header: 'UM', visible: true, width: '12%'},
            //     smallScreen: {header: 'UM', visible: true, width: '55px'},
            //     export: {visible: true, checked: true}
            // },
            {
                field: 'PO2_UM_NEW', align: 'center', sortable: false,
                bigScreen: {header: 'UM', visible: true, width: '55px'},
                smallScreen: {header: 'UM', visible: true, width: '55px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_LAST_SHIPMENT_DATE', align: 'center', sortable: true,
                bigScreen: {header: 'Last Shipment Date', visible: true, width: '80px'},
                smallScreen: {header: 'Last Shipped', visible: true, width: '80px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_LAST_ARRIVAL_DATE', align: 'center', sortable: true,
                bigScreen: {header: 'Last Arrival Date', visible: true, width: '80px'},
                smallScreen: {header: 'Last Arrival', visible: true, width: '80px'},
                export: {visible: true, checked: true}
            },
            {
                field: 'PO2_LAST_INVOICING_DATE', align: 'center', sortable: true,
                bigScreen: {header: 'Last Invoice Date', visible: true, width: '80px'},
                smallScreen: {header: 'Last Invoiced', visible: true, width: '80px'},
                export: {visible: true, checked: true}
            }
        ];
        this.onWindowResize();

        this.subscriptions = [];
        this.subscriptions.push(
            this.orderLineService.preloadData().subscribe(data => {
                this.prepareData(data);
                this.setPage({offset: 0});
            })
        );
    }

    ngOnDestroy() {
        if (this.subscriptions.length > 0) {
            for (let index = 0; index < this.subscriptions.length; index++) {
                this.subscriptions[index].unsubscribe();
            }
        }
    }

    prepareData(data) {
        // this.units = data.units.map(unit => {
        //     return {
        //         UM1_ID: unit.UM1_ID,
        //         UM1_NAME: unit.UM1_NAME
        //     };
        // });
        // this.filteredUnits = this.units.slice();

        this.vendors = data.vendors.map(vendor => {
            return {
                CO1_ID    : vendor.CO1_ID,
                VD1_ID    : vendor.VD1_ID,
                VENDOR_ID : vendor.VENDOR_ID,
                VD1_NAME  : vendor.VD1_NAME
            };
        });
        this.filteredVendors = JSON.parse(JSON.stringify(this.vendors));
    }

    applyFilters() {
        const filters = _.clone(this.filters);
        for (const filterKey of Object.keys(filters)) {
            if (filters[filterKey] == null || filters[filterKey] === undefined || filters[filterKey] === '') {
                delete filters[filterKey];
            }
        }
        this.filtersApplied = Object.keys(filters).length > 0;
        this.page.filterParams = filters;
        this.setPage({offset: 0});
    }

    clearFilters() {
        this.filters = {};
        this.showFilters = false;

        this.linesTable.sortOrder = 0;
        this.linesTable.sortField = '';
        this.linesTable.reset();

        this.applyFilters();
    }

    filterKeyPress(event) {
        event.target.blur();
        this.applyFilters();
    }

    _filterVendors(event) {
        const filterValue = (event === undefined) ? '' : event.toUpperCase();
        const vendors = JSON.parse(JSON.stringify(this.vendors));
        this.filteredVendors = vendors.filter(vendor => vendor.VD1_NAME.toUpperCase().indexOf(filterValue) === 0).slice();
    }

    // _filterUnits(event) {
    //     const filterValue = (event === undefined) ? '' : event.toUpperCase();
    //     this.filteredUnits = this.units.filter(unit => unit.UM1_NAME.toUpperCase().indexOf(filterValue) === 0).slice();
    // }

    loadPO(row) {
        this.router.navigate(['order-headers/' + row.PO1_ID]);
    }

    onPage(event) {
        this.page.size = event.rows;
        this.setPage({
            offset: event.first / event.rows
        });
    }

    onSort(col) {
        if (col.sortable) {
            if (this.filters.hasOwnProperty('SORT_COLUMN')) {
                if (this.filters['SORT_COLUMN'] === col.field) {
                    if (this.filters['SORT_DIRECTION'] === 'ASC') {
                        this.filters['SORT_DIRECTION'] = 'DESC';
                    } else {
                        this.filters['SORT_DIRECTION'] = 'ASC';
                    }
                } else {
                    this.filters['SORT_COLUMN'] = col.field;
                    this.filters['SORT_DIRECTION'] = 'ASC';
                }
            } else {
                this.filters['SORT_COLUMN'] = col.field;
                this.filters['SORT_DIRECTION'] = 'ASC';
            }
            this.showFilters = true;
            this.applyFilters();
        }
    }

    onUpdateRequest() {
        this.confirmationService.confirm({
            header: 'Request Update Action',
            message: 'Are you sure to Request Update for selected POs?',
            accept: () => {
                const PO1_IDs = this.selected.map(selected => {
                    return selected['PO1_ID']
                });
                this.subscriptions.push(
                    this.orderHeadersService.resend(PO1_IDs, true).subscribe(result => {
                        let message = '';
                        result.results.forEach( item => {
                            if (item.success) {
                                message += 'Update requested for PO #' + item.PO1_NUMBER + '.' + '<br/>';
                            }
                        });
                        this.errors = [];
                        this.toastr.success(message, 'Success!', {
                            timeOut: 10000,
                            enableHtml: true
                        });
                    })
                );
            }
        });
    }

    onWindowResize() {
        this.cols.forEach(col => {
            const isBigScreen = window.innerWidth >= environment.smallScreenThreshold;
            col.visible = isBigScreen ? col.bigScreen.visible : col.smallScreen.visible;
            col.header = isBigScreen ? col.bigScreen.header : col.smallScreen.header;
            col.width = isBigScreen ? col.bigScreen.width : col.smallScreen.width;
        });
    }

    refreshPage() {
        this.setPage({offset: this.page.pageNumber});
    }

    setPage(pageInfo) {
        this.loading = true;
        this.page.pageNumber = pageInfo.offset;

        this.subscriptions.push(
            this.orderLineService.getItems(this.page).subscribe(result => {
                this.rows = result.data;
                this.totalRecords = result.count;
                this.loading = false;
            }, error => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Time-out occurred in fetching data', 'Service Error', {
                    timeOut: 3000,
                });
            })
        );
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }

    exportClick() {
        const cols = this.cols.map((el) => {
            el.header = el.bigScreen.header;
            return el;
        });
        const dialogRef = this.dialog.open(ExportModalComponent, {
            width: window.innerWidth - 100 + 'px',
            height: window.innerHeight - 80 + 'px',
            role: 'dialog',
            data: {cols: cols, modeEnabled: 0},
        });

        dialogRef.beforeClose().subscribe(preferences => {
            if (preferences) {
                this.orderLineService.export(preferences.columns, this.page, 'PurchaseLines.xls').subscribe();
            }
        });
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/order-lines/order-lines.component.ts